import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { MaskedLines } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const Hero = ({ onNavigate }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-forest-deep text-cream overflow-hidden"
    >
      {/* Full-bleed background construction photo with green tint */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <motion.img
          src="/hero/cantiere.jpg"
          alt="Cantiere I.E.P.E.M."
          className="h-full w-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: EASE }}
        />
      </motion.div>
      {/* Green duotone overlay */}
      <div className="absolute inset-0 bg-forest-deep/75 mix-blend-multiply" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/90 via-forest-deep/55 to-forest-deep/70" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-transparent to-forest-deep/40" aria-hidden />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 min-h-screen flex flex-col justify-center pt-32 pb-14"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="flex items-center gap-4 mb-7"
        >
          <span className="w-10 h-px bg-gold" />
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.32em] text-gold">
            {t("hero.kicker")}
          </span>
        </motion.div>

        <h1 className="font-sans font-extrabold italic tracking-tight leading-[0.98] text-[2.7rem] sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl">
          <MaskedLines
            lines={[t("hero.line1"), t("hero.line2"), t("hero.line3")]}
            start={0.35}
            lineClassName="pb-1"
          />
          <span className="mask-line">
            <motion.span
              className="block text-gold pb-1"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: EASE, delay: 0.71 }}
            >
              {t("hero.lastLine")}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: EASE }}
          className="mt-8 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
        >
          {t("hero.intro")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-6"
        >
          <button
            onClick={() => onNavigate && onNavigate("lavori:petrolifero")}
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-3 rounded-sm bg-gold text-forest-deep px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-cream transition-colors duration-300"
          >
            {t("hero.ctaPrimary")}
            <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform duration-300" />
          </button>
          <button
            onClick={() => onNavigate && onNavigate("contatti")}
            data-testid="hero-cta-secondary"
            className="relative text-sm font-bold uppercase tracking-wider text-cream pb-1 after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:bg-cream/50 hover:after:bg-gold hover:text-gold transition-colors duration-300"
          >
            {t("hero.ctaSecondary")}
          </button>
        </motion.div>
      </motion.div>

      {/* scroll indicator bottom-right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-6 sm:right-10 lg:right-14 z-10 flex items-center gap-3"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.4em] text-cream/60">
          {t("hero.scroll")}
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-gold" />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
